import { useTranslation } from "next-i18next";
import Link from "next/link";
import PricingProducts from "../components/pricing-tabs";
import Accordion from "../components/v2/accordion/accordion";
import CallToActionSmall from "../components/v2/call-to-action-sm";
import PricingCalculator from "../components/v2/pricing-calculator";
import Tooltip from "../components/v2/tooltip";
import "../styles/scss/pricing.module.scss";
import '../styles/scss/_faq-section.module.scss';

/** Matches the info tag used by the pricing calculator's tooltips. */
const FeatureInfoIcon = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="7" stroke="#5B6471" strokeWidth="1.2" />
        <path d="M8 7V11" stroke="#5B6471" strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="8" cy="5" r="0.75" fill="#5B6471" />
    </svg>
);

export default function Pricing() {
    const { t } = useTranslation('common');
    const pricingData = t('pricing', { returnObjects: true, defaultValue: {} });

    const heroData = pricingData?.hero || {};
    const cardsData = Array.isArray(pricingData?.cards) ? pricingData.cards : [];
    const opsAIData = pricingData?.opsAI || {};
    const faqData = pricingData?.faq || {};
    const faqs = Array.isArray(faqData?.items) ? faqData.items.map((item) => ({
        id: item.id,
        header: String(item.header ?? ''),
        text: <div dangerouslySetInnerHTML={{ __html: String(item.text ?? '') }} />
    })) : [];


    return (
        <div className="pricing-page-v2">

            <div className={"pricing-hero"}>
                <div className={"container"}>
                    <h1><span className={"text-gradient"}>{String(heroData?.titlePrefix ?? '')}</span>{String(heroData?.titleHighlight ?? '')}</h1>
                    <p>{String(heroData?.description ?? '')}</p>
                    <div className={"pricing-card"}>
                        {cardsData.map((card, index) => (
                            <div key={index} className={"card"}>
                                <div className={"header-item"}>
                                    <h4>{String(card?.title ?? '')}</h4>
                                    <h2 className={"text-gradient"}>
                                        {String(card?.price ?? '')}
                                        {card?.priceSubtext && <span> {String(card.priceSubtext)}</span>}
                                    </h2>
                                    <p>{String(card?.description ?? '')}</p>
                                    <Link
                                        href={String(card?.cta?.link ?? '')}
                                        target={card?.cta?.link?.includes('http') ? "_blank" : undefined}
                                        className={"ahref-btn primary"}
                                    >
                                        {String(card?.cta?.text ?? '')}
                                    </Link>
                                </div>
                                {/* <p>{String(card?.featuresLabel ?? card?.includesLabel ?? '')}</p> */}
                                <ul>
                                    {/* A feature is either a plain string or
                                        { text, tooltip } when it needs an info tag. */}
                                    {Array.isArray(card?.features) ? card.features.map((feature, idx) => {
                                        const label = typeof feature === "object" && feature !== null
                                            ? String(feature.text ?? '')
                                            : String(feature);
                                        const tooltip = typeof feature === "object" && feature !== null
                                            ? feature.tooltip
                                            : null;
                                        return (
                                            <li key={idx} className={idx === 0 ? "list-after-icon" : ""}>
                                                {label}
                                                {tooltip ? (
                                                    <Tooltip content={String(tooltip)} position="top">
                                                        <button
                                                            type="button"
                                                            className="pricing-feature-info"
                                                            aria-label={String(tooltip)}
                                                        >
                                                            <FeatureInfoIcon />
                                                        </button>
                                                    </Tooltip>
                                                ) : null}
                                            </li>
                                        );
                                    }) : null}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className={"pricing-pre-deploy"}>
                <div className={"container"}>
                    <div className={"d-flex"}>
                        <div className={"premise-deploy"}>
                            <h2 className={"text-gradient"}>{String(opsAIData?.title ?? '')}</h2>
                        </div>
                        <div className={"premise-list"}>
                            {Array.isArray(opsAIData?.features?.left) && (
                                <ul>
                                    {opsAIData.features.left.map((feature, idx) => (
                                        <li key={idx}>{String(feature)}</li>
                                    ))}
                                </ul>
                            )}
                            {Array.isArray(opsAIData?.features?.right) && (
                                <ul>
                                    {opsAIData.features.right.map((feature, idx) => (
                                        <li key={idx}>{String(feature)}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className={"premise-contact"}>
                            <a href="#opsai" className="ahref-btn glossy-btn">View Detailed Pricing</a>
                        </div>
                    </div>
                </div>
            </div>

            <PricingCalculator/>

            <PricingProducts/>
            <section className={"faq-section"}>
                <div className={"container"}>
                    <h2>{String(faqData?.title ?? '')}</h2>
                    <p>{String(faqData?.description ?? '')}</p>
                    <div className={"faq-item"}>
                        <Accordion faqs={faqs} showGroups={true} listKey={4}/>
                    </div>
                </div>
            </section>
            <CallToActionSmall isDemoBtn={true}/>
        </div>
    )
}
